// Lote C: ordenamiento genérico de tablas de catálogo — generaliza el patrón de ingredientSort.ts
// (que se mantiene tal cual por sus reglas específicas). Cada tabla define sus comparadores; aquí
// solo viven la mecánica del clic y el collator compartido.

export interface TableSort<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

// sensitivity 'base' => acentos ordenan juntos; numeric => "Vaso 2" antes que "Vaso 10".
export const tableCollator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

/** Mismo contrato que nextIngredientSort: misma columna alterna dirección, otra arranca asc. */
export function nextTableSort<K extends string>(current: TableSort<K>, key: K): TableSort<K> {
  return { key, dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc' };
}

/**
 * Copia ordenada (no muta lo que viene del API). `comparators[key]` compara ascendente; el desempate
 * lo pone cada tabla (normalmente por nombre) para que el orden sea estable dentro de cada grupo.
 */
export function sortRows<T, K extends string>(
  rows: T[],
  sort: TableSort<K>,
  comparators: Record<K, (a: T, b: T) => number>,
  tiebreak: (a: T, b: T) => number,
): T[] {
  return [...rows].sort((a, b) => {
    const result = comparators[sort.key](a, b);
    const oriented = sort.dir === 'asc' ? result : -result;
    return oriented !== 0 ? oriented : tiebreak(a, b);
  });
}

/**
 * Cómo se LEE cada orden: «Fecha (más reciente primero)», «Tienda (Z–A)».
 *
 * Cada pantalla trae su tabla porque los nombres de sus columnas son suyos, y porque cada tipo de
 * dato se lee distinto: una fecha no se lee como un importe ni como un texto. Una sola entrada por
 * columna —nombre, cómo suena ascendente, cómo suena descendente— alimenta a la vez el selector de
 * móvil y la etiqueta del orden actual, así que no pueden desincronizarse.
 */
export type SortWording<K extends string> = Record<K, readonly [name: string, asc: string, desc: string]>;

export function sortChoiceLabel<K extends string>(wording: SortWording<K>, sort: TableSort<K>): string {
  const [name, asc, desc] = wording[sort.key];
  return `${name} (${sort.dir === 'asc' ? asc : desc})`;
}

/**
 * Las opciones del selector de orden de móvil, con el orden ACTUAL garantizado dentro.
 *
 * La lista curada de cada pantalla es corta a propósito: ofrecer las 2×N combinaciones convertiría
 * el desplegable en una pared. Pero eso significa que el orden actual puede NO estar en ella, y pasa
 * con solo ordenar por un encabezado en escritorio y estrechar después la ventana: el `value` del
 * <select> no casa con ninguna <option> y el navegador lo pinta EN BLANCO — diciendo que no hay
 * orden cuando sí lo hay, y sin manera de saber cuál.
 *
 * Cuando el orden actual no está en la lista, se antepone como una opción más. El desplegable pasa a
 * decir siempre la verdad, y no hace falta inflar la lista curada.
 */
export function sortChoices<K extends string>(
  wording: SortWording<K>,
  curated: ReadonlyArray<readonly [K, 'asc' | 'desc']>,
  current: TableSort<K>,
): Array<{ value: string; label: string }> {
  const opciones = curated.map(([key, dir]) => ({ value: `${key}:${dir}`, label: sortChoiceLabel(wording, { key, dir }) }));
  const actual = `${current.key}:${current.dir}`;
  return opciones.some((opcion) => opcion.value === actual)
    ? opciones
    : [{ value: actual, label: sortChoiceLabel(wording, current) }, ...opciones];
}

/**
 * P-076 (Codex R2 · H2): el selector de orden del teléfono ofrece TODAS las claves de la tabla en sus dos direcciones,
 * en el orden en que las declara el `wording` (que sigue el de los encabezados). Una lista corta a mano perdía
 * combinaciones que los encabezados de escritorio sí permiten.
 */
export function allSortChoices<K extends string>(wording: SortWording<K>): Array<readonly [K, 'asc' | 'desc']> {
  return (Object.keys(wording) as K[]).flatMap((key) => [[key, 'asc'] as const, [key, 'desc'] as const]);
}
